// Magic-link redemption page. Route: /auth/magic?token=...
//
// Flow: read ?token= from URL → GET /api/auth/magic-link/verify → store JWT →
// persist quiz_state (if server returned any) → route to /welcome (new users)
// or /dashboard (existing).
//
// Failure mode: if the token is expired, consumed, or malformed, show a small
// error card with a button back to /onboarding. Deliberately does NOT auto-
// send another link — the request happens from RegisterGate where the visitor
// still has the quiz state.
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Zap, AlertCircle } from "lucide-react";

export default function MagicLinkVerify() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { checkAuth } = useAuth();
    const [status, setStatus] = useState("verifying");   // verifying | error
    const [errorMsg, setErrorMsg] = useState("");
    // React 18 StrictMode double-invokes effects in dev. A useRef guard is
    // the sanctioned pattern for "run exactly once" side-effect effects
    // whose payload is single-use (magic-link tokens are consumed on the
    // server the first hit; a second hit gets 400 "already used"). Without
    // this guard, dev-preview users would always land on the error screen
    // even for freshly-issued valid tokens.
    const ranRef = useRef(false);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        const token = params.get("token") || "";
        if (!token) {
            setStatus("error");
            setErrorMsg("No token in the link.");
            return;
        }
        (async () => {
            try {
                const r = await api.get(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`);
                const { token: jwt, user, quiz_state, is_new_user } = r.data;
                // Persist JWT FIRST — before any conditional gates or async
                // work — so a successful token redemption always sticks even
                // if the component unmounts mid-flight.
                localStorage.setItem("hg_token", jwt);
                if (quiz_state && Object.keys(quiz_state).length > 0) {
                    try {
                        localStorage.setItem("hg_magic_quiz_state", JSON.stringify(quiz_state));
                    } catch { /* private mode — non-fatal */ }
                }
                if (typeof checkAuth === "function") { await checkAuth(); }
                if (is_new_user || !user?.onboarded) {
                    navigate("/onboarding", { replace: true });
                } else {
                    navigate("/dashboard", { replace: true });
                }
            } catch (err) {
                setStatus("error");
                setErrorMsg(err?.response?.data?.detail || "This link is expired or already used.");
            }
        })();
    }, [params, navigate, checkAuth]);

    if (status === "verifying") {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-4 text-white" data-testid="magic-verifying">
                <div className="text-center">
                    <Zap className="mx-auto h-8 w-8 animate-pulse text-accent-red" />
                    <p className="mt-4 font-display text-xl font-black uppercase">Logging you in…</p>
                    <p className="mt-2 text-xs uppercase tracking-widest text-white/50">One second.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-4 text-white" data-testid="magic-error-screen">
            <div className="w-full max-w-md border border-white/10 bg-[#141414] p-6">
                <div className="flex items-center gap-2 text-accent-red">
                    <AlertCircle className="h-5 w-5" />
                    <span className="label-eyebrow">Link expired</span>
                </div>
                <h1 className="mt-3 font-display text-2xl font-black uppercase leading-tight">Grab a fresh link.</h1>
                <p className="mt-2 text-sm text-white/70">{errorMsg}</p>
                <Link
                    to="/onboarding"
                    data-testid="magic-back-to-onboarding"
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-sm bg-accent-red px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]"
                >
                    Back to onboarding
                </Link>
            </div>
        </div>
    );
}
